import { useCallback, useEffect, useState } from 'react';
import { TbPhotoOff } from 'react-icons/tb';
import './training.css';

const SIZES = ['sm', 'md', 'lg'];
const LOAD_TIMEOUT_MS = 7000;

function initialStageFor(exercise) {
  if (exercise && exercise.gifUrl) return 'gif';
  if (exercise && exercise.imageUrl) return 'image';
  return 'placeholder';
}

/**
 * Exercise media (spec "Catálogo con GIFs"): a lazy `<img>` of the animated
 * demonstration, with a skeleton while it loads and a two-step fallback —
 * `gifUrl` fails -> `imageUrl`; that fails too (or neither URL exists) -> a
 * static placeholder icon, never a broken-image glyph. `size` only toggles a
 * CSS class (`training.css` sizes the box); this component never measures
 * pixels itself.
 */
export function ExerciseGif({ exercise, size = 'md', loading = 'lazy' }) {
  const sizeClass = SIZES.includes(size) ? size : 'md';
  const gifUrl = exercise && exercise.gifUrl;
  const imageUrl = exercise && exercise.imageUrl;
  const name = (exercise && exercise.name) || 'Ejercicio';
  const [stage, setStage] = useState(() => initialStageFor(exercise));
  const [loaded, setLoaded] = useState(false);

  // A different exercise (grid reuse, e.g. the picker recycling DOM nodes)
  // must restart the fallback chain from its own gifUrl, not keep whatever
  // stage the previous exercise settled into.
  useEffect(() => {
    setStage(initialStageFor(exercise));
    setLoaded(false);
  }, [gifUrl, imageUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleError = useCallback(() => {
    setLoaded(false);
    setStage((current) => (current === 'gif' && imageUrl ? 'image' : 'placeholder'));
  }, [imageUrl]);

  useEffect(() => {
    if (stage === 'placeholder' || loaded) return undefined;
    const timeout = setTimeout(handleError, LOAD_TIMEOUT_MS);
    return () => clearTimeout(timeout);
  }, [stage, loaded, gifUrl, imageUrl, handleError]);

  function handleLoad() {
    setLoaded(true);
  }

  const src = stage === 'gif' ? gifUrl : stage === 'image' ? imageUrl : null;

  return (
    <span className={`gym-ex-gif gym-ex-gif--${sizeClass}`}>
      {stage !== 'placeholder' && !loaded && <span className="gym-ex-gif__skeleton" aria-hidden="true" />}
      {stage === 'placeholder' ? (
        <span className="gym-ex-gif__placeholder" role="img" aria-label={`${name} — demostración no disponible`}>
          <TbPhotoOff aria-hidden="true" focusable="false" />
        </span>
      ) : (
        <img
          className={`gym-ex-gif__img${loaded ? ' is-loaded' : ''}`}
          src={src}
          alt={`${name} — demostración`}
          loading={loading === 'eager' ? 'eager' : 'lazy'}
          onLoad={handleLoad}
          onError={handleError}
        />
      )}
    </span>
  );
}
